import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectContentWithSearch, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Separator } from './ui/separator';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { ArrowLeft, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { physicalMachineApiService, PhysicalMachine, PhysicalMachineCreateRequest, PhysicalMachineUpdateRequest } from '../services/physicalMachineApi';
import { branchApiService, Branch } from '../services/branchApi';
import { departmentApiService, Department } from '../services/departmentApi';
import { workAreaApiService, WorkArea } from '../services/workAreaApi';
import { itemCategoryApiService, ItemCategory } from '../services/itemCategoryApi';
import { useBranch } from '../contexts/BranchContext';

export function PhysicalMachineInformationForm() {
	const navigate = useNavigate();
	const { id } = useParams<{ id: string }>();
	const { selectedBranch } = useBranch();
	const [current, setCurrent] = useState<PhysicalMachine | null>(null);
	const [viewMode, setViewMode] = useState(false);
	const [branches, setBranches] = useState<Branch[]>([]);
	const [departments, setDepartments] = useState<Department[]>([]);
	const [workAreas, setWorkAreas] = useState<WorkArea[]>([]);
	const [categories, setCategories] = useState<ItemCategory[]>([]);
	const [loading, setLoading] = useState(false);
	const [isAddDepartmentOpen, setIsAddDepartmentOpen] = useState(false);
	const [newDepartment, setNewDepartment] = useState('');

	const [form, setForm] = useState<PhysicalMachineCreateRequest>({
		machineCode: '',
		machineName: '',
		erpCode: '',
		machineModel: '',
		machineEff: '',
		categoryId: undefined,
		usedFor: '',
		smp: '',
		branchId: selectedBranch || undefined,
		departmentId: undefined,
		workAreaId: undefined,
		isActive: true,
	});

	useEffect(() => {
		if (id && window.location.pathname.includes('/view')) setViewMode(true);
		if (id && window.location.pathname.includes('/edit')) setViewMode(false);
	}, [id]);

	useEffect(() => {
		const init = async () => {
			try {
				const [branchList, deptList, workAreaList, categoryList] = await Promise.all([
					branchApiService.getBranches(),
					selectedBranch ? departmentApiService.getActiveByBranch(selectedBranch) : Promise.resolve([]),
					selectedBranch ? workAreaApiService.getActiveByBranch(selectedBranch) : Promise.resolve([]),
					itemCategoryApiService.getItemCategories()
				]);
				setBranches(branchList);
				setDepartments(deptList as Department[]);
				setWorkAreas(workAreaList as WorkArea[]);
				setCategories(categoryList);

				if (id) {
					const m = await physicalMachineApiService.getMachineById(id);
					setCurrent(m);
					
					setForm({
						machineCode: m.machineCode,
						machineName: m.machineName,
						erpCode: m.erpCode || '',
						machineModel: m.machineModel || '',
						machineEff: m.machineEff || '',
						categoryId: m.categoryId,
						usedFor: m.usedFor || '',
						smp: m.smp || '',
						branchId: m.branch?.id ?? m.branchId,
						departmentId: m.departmentId,
						workAreaId: m.workAreaId,
						isActive: m.isActive ?? true,
					});
				}
				// create: machineCode left empty; do not call generate-code API
			} catch (e) {
				// already toasted
			}
		};
		init();
	}, [id, selectedBranch]);
	
	// Reset department and workArea selections if branch changed and they're no longer valid
	useEffect(() => {
		if (!id && selectedBranch && form.departmentId) {
			const deptExists = departments.some(d => d.id === form.departmentId);
			if (!deptExists) {
				setForm(prev => ({ ...prev, departmentId: undefined }));
			}
		}
		if (!id && selectedBranch && form.workAreaId) {
			const waExists = workAreas.some(w => w.id === form.workAreaId);
			if (!waExists) {
				setForm(prev => ({ ...prev, workAreaId: undefined }));
			}
		}
	}, [selectedBranch, departments, workAreas, id]);

	const handleChange = (key: keyof PhysicalMachineCreateRequest, value: string | number | boolean | undefined) => {
		setForm((prev: PhysicalMachineCreateRequest) => ({ ...prev, [key]: value } as PhysicalMachineCreateRequest));
	};

	const handleSave = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!form.machineName) {
			toast.error('Please fill in Physical Machine Name');
			return;
		}
		setLoading(true);
		try {
			// Build payload with only IDs, exclude undefined values
			const payload: PhysicalMachineCreateRequest = {
				machineCode: form.machineCode?.trim() || undefined,
				machineName: form.machineName,
				erpCode: form.erpCode || undefined,
				machineModel: form.machineModel || undefined,
				machineEff: form.machineEff || undefined,
				categoryId: form.categoryId || undefined,
				usedFor: form.usedFor || undefined,
				smp: form.smp || undefined,
				branchId: form.branchId || undefined,
				departmentId: form.departmentId || undefined,
				workAreaId: form.workAreaId || undefined,
				isActive: form.isActive ?? true,
			};
			
			if (current && current.id) {
				await physicalMachineApiService.updateMachine(String(current.id), { ...payload, id: current.id } as PhysicalMachineUpdateRequest);
				toast.success('Physical machine updated');
			} else {
				await physicalMachineApiService.createMachine(payload);
				toast.success('Physical machine created');
			}
			navigate('/physical-machines');
		} catch (e) {
			// handled
		} finally {
			setLoading(false);
		}
	};

	const handleCancel = () => navigate('/physical-machines');

	return (
		<Card className="w-full border-0 shadow-lg bg-white/70 backdrop-blur-sm">
			<CardHeader className="bg-gradient-to-r from-primary-50 to-primary-100/50 rounded-t-lg">
				<div className="flex items-center gap-4">
					<Button
						variant="ghost"
						size="sm"
						onClick={handleCancel}
						className="text-gray-700 hover:bg-gray-50"
					>
						<ArrowLeft className="h-4 w-4 mr-2" />
						Back
					</Button>
					<div>
						<CardTitle className="text-xl text-gray-900">
							{viewMode ? 'View Physical Machine' : current ? 'Edit Physical Machine' : 'Add Physical Machine'}
						</CardTitle>
					</div>
				</div>
			</CardHeader>

			<CardContent className="p-6">
				<form onSubmit={handleSave} className="space-y-8">
					<div className="space-y-6">
						<div className="space-y-4">
							<h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
								Basic Information
							</h3>
							<Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
							<div className="space-y-2">
								<Label>Machine Code</Label>
								<Input
									value={form.machineCode || ''}
									onChange={(e) => handleChange('machineCode', e.target.value)}
									placeholder="Enter machine code"
									className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
									disabled={viewMode}
								/>
							</div>
							<div className="space-y-2">
								<Label>
									Physical Machine Name <span className="text-red-500">*</span>
								</Label>
								<Input
									value={form.machineName}
									onChange={(e) => handleChange('machineName', e.target.value)}
									placeholder="Enter physical machine name"
									className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
									disabled={viewMode}
									required
								/>
							</div>

							<div className="space-y-2">
								<Label>ERP Code</Label>
								<Input
									value={form.erpCode || ''}
									onChange={(e) => handleChange('erpCode', e.target.value)}
									placeholder="Enter ERP code"
									className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
									disabled={viewMode}
								/>
							</div>
							<div className="space-y-2">
								<Label>Model</Label>
								<Input
									value={form.machineModel || ''}
									onChange={(e) => handleChange('machineModel', e.target.value)}
									placeholder="Enter model"
									className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
									disabled={viewMode}
								/>
							</div>

							<div className="space-y-2">
								<Label>Machine Eff</Label>
								<Input
									value={form.machineEff || ''}
									onChange={(e) => handleChange('machineEff', e.target.value)}
									placeholder="Enter machine efficiency"
									className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
									disabled={viewMode}
								/>
							</div>
							<div className="space-y-2">
								<Label>Category</Label>
								<Select
									value={form.categoryId ? String(form.categoryId) : ''}
									onValueChange={(val) => handleChange('categoryId', Number(val))}
									disabled={viewMode}
								>
									<SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
										<SelectValue placeholder="Select category" />
									</SelectTrigger>
									<SelectContent>
										{categories.filter(c => c.isActive).map(c => (
											<SelectItem key={c.id} value={String(c.id)}>{c.categoryName}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>

							<div className="space-y-2">
								<Label>Used For</Label>
								<Input
									value={form.usedFor || ''}
									onChange={(e) => handleChange('usedFor', e.target.value)}
									placeholder="Enter usage"
									className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
									disabled={viewMode}
								/>
							</div>
							<div className="space-y-2">
								<Label>SMP</Label>
								<Input
									value={form.smp || ''}
									onChange={(e) => handleChange('smp', e.target.value)}
									placeholder="Enter SMP"
									className="border-primary-200 focus:border-primary-400 focus:ring-primary-400"
									disabled={viewMode}
								/>
							</div>
						</div>
					</div>

					<div className="space-y-6">
						<div className="space-y-4">
							<h3 className="text-lg font-semibold bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">
								Assignment
							</h3>
							<Separator className="bg-gradient-to-r from-primary-200 to-primary-300 h-0.5" />
						</div>

						<div className="grid grid-cols-1 md:grid-cols-2 gap-6">
						<div className="space-y-2">
							<Label>Branch</Label>
							<Select
								value={form.branchId ? String(form.branchId) : ''}
								onValueChange={(val) => handleChange('branchId', Number(val))}
								disabled={viewMode || !current}
							>
								<SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
									<SelectValue placeholder="Select branch" />
								</SelectTrigger>
								<SelectContent>
									{branches.map(b => (
										<SelectItem key={b.id} value={String(b.id)}>{b.branchName}</SelectItem>
									))}
								</SelectContent>
							</Select>
						</div>
						<div className="space-y-2">
							<Label>Department</Label>
							<div className="flex gap-2">
							<Select
								value={form.departmentId ? String(form.departmentId) : ''}
								onValueChange={(val) => handleChange('departmentId', Number(val))}
								disabled={viewMode}
							>
								<SelectTrigger className="flex-1 border-primary-200 focus:border-primary-400 focus:ring-primary-400">
									<SelectValue placeholder={selectedBranch ? 'Select department' : 'Select a branch first'} />
								</SelectTrigger>
								<SelectContentWithSearch searchPlaceholder="Search departments...">
									{departments.map(d => (
										<SelectItem key={d.id} value={String(d.id)}>{d.name}</SelectItem>
									))}
								</SelectContentWithSearch>
							</Select>
								{!viewMode && (
									<Dialog open={isAddDepartmentOpen} onOpenChange={setIsAddDepartmentOpen}>
										<Button type="button" size="icon" variant="outline" className="border-primary-200 hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-100 hover:border-primary-300" onClick={() => setIsAddDepartmentOpen(true)}>
											<Plus className="h-4 w-4 text-primary-600" />
										</Button>
										<DialogContent className="border-primary-200">
											<DialogHeader>
												<DialogTitle className="bg-gradient-to-r from-primary-700 to-primary-800 bg-clip-text text-transparent">Add New Department</DialogTitle>
												<DialogDescription>Enter a new department name.</DialogDescription>
											</DialogHeader>
											<div className="space-y-4">
												<div className="space-y-2">
													<Label htmlFor="newDepartment">Department Name</Label>
													<Input id="newDepartment" value={newDepartment} onChange={(e) => setNewDepartment(e.target.value)} placeholder="e.g., Production" className="border-primary-200 focus:border-primary-400 focus:ring-primary-400" />
												</div>
											</div>
											<DialogFooter>
												<Button type="button" variant="outline" onClick={() => setIsAddDepartmentOpen(false)} className="border-primary-200 hover:bg-primary-50">Cancel</Button>
												<Button type="button" onClick={async () => {
													const value = newDepartment.trim();
													if (!value) {
														toast.error('Please enter department name');
														return;
													}
													
													// Check if department already exists
													if (departments.some(d => d.name.toLowerCase() === value.toLowerCase())) {
														toast.error('Department already exists');
														return;
													}
													
													try {
														// Call backend API to create department
														await departmentApiService.createDepartment({
															name: value,
															code: value, // Keep code same as name
															isActive: true,
															branchId: selectedBranch || undefined
														});
														
														// Refresh department list
														if (selectedBranch) {
															const updatedDepartments = await departmentApiService.getActiveByBranch(selectedBranch);
															setDepartments(updatedDepartments as Department[]);
														}
														
														setNewDepartment('');
														setIsAddDepartmentOpen(false);
													} catch (error) {
														// Error is already handled by the API service
														// console.error('Failed to create department:', error);
													}
												}} className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white shadow-lg">Add Department</Button>
											</DialogFooter>
										</DialogContent>
									</Dialog>
								)}
							</div>
						</div>
							<div className="space-y-2">
								<Label>Work Area</Label>
								<Select
									value={form.workAreaId ? String(form.workAreaId) : ''}
									onValueChange={(val) => handleChange('workAreaId', Number(val))}
									disabled={viewMode}
								>
									<SelectTrigger className="border-primary-200 focus:border-primary-400 focus:ring-primary-400">
										<SelectValue placeholder={selectedBranch ? 'Select work area' : 'Select a branch first'} />
									</SelectTrigger>
									<SelectContent>
										{workAreas.map(w => (
											<SelectItem key={w.id} value={String(w.id)}>{w.workAreaName}</SelectItem>
										))}
									</SelectContent>
								</Select>
							</div>
						</div>
					</div>

					{!viewMode && (
						<div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
							<Button
								type="button"
								variant="outline"
								onClick={handleCancel}
								className="border-primary-200 text-primary-600 hover:bg-primary-50"
							>
								Cancel
							</Button>
							<Button
								type="submit"
								className="bg-gradient-to-r from-primary-500 to-primary-600 hover:from-primary-600 hover:to-primary-700 text-white"
								disabled={loading}
							>
								{loading ? 'Saving...' : (current ? 'Update Physical Machine' : 'Create Physical Machine')}
							</Button>
						</div>
					)}

					{viewMode && (
						<div className="flex justify-end gap-4 pt-6 border-t border-primary-200">
							<Button
								type="button"
								variant="outline"
								onClick={handleCancel}
								className="border-primary-200 text-primary-600 hover:bg-primary-50"
							>
								Close
							</Button>
						</div>
					)}
				</form>
			</CardContent>
		</Card>
	);
}


